import { ANIMATION_SETTINGS } from "../settings/animation";

type SlideDrawerDirection = 'left' | 'right';
type SlideDrawerWidth = 'small' | 'large';

const CLASS_CONTAINER = 'o-slide-drawer';
export const CLASS_OPEN = 'is-slide-drawer-open';

const SEL_TRIGGER = '[data-slide-drawer-trigger]';
const SEL_CLOSE = '[data-close]';
const SEL_TITLE = '[data-title]';
const ATTR_TRIGGER = 'data-slide-drawer-trigger';

const template = document.createElement('template');
template.innerHTML = `
  <style>
    * { box-sizing: border-box; }
  
    .container {
      position: fixed;
      display: none;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      overflow: hidden;
    }
    .inner {
      position: relative;
      width: 100%;
      height: 100%;
    }
    .background {
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      opacity: 0;
      transition: all ${ANIMATION_SETTINGS.timeDefault} ${ANIMATION_SETTINGS.easeOut};
      cursor: pointer;
      background-image: var(--bg-image);
    }
    .drawer {
      height: 100%;
      display: flex;
      flex-direction: column;
      position: relative;
      transition: all ${ANIMATION_SETTINGS.timeDefault} ${ANIMATION_SETTINGS.easeOut};
    }
    .is-hidden{
      display:none;
    }
    .close {
      position: absolute;
      top: 1.25rem;
      left: 1.25rem;
      z-index: 3;
      padding: 0.5rem;
      border: 1px solid rgba(18, 50, 71, 0.2);
      border-radius: 100%;
      background: transparent;
      cursor: pointer;
    }
    .close svg {
      width: 0.75rem;
      display: block;
    }
    .o-bundle-builder-slide .close {
      border: 1px solid white;
    }
    .o-bundle-builder-slide .close path {
      stroke: white;
    }
    .left {
      margin-right: auto;
      transform: translateX(-100%);
    }
    .left .close {
      right: 1.25rem;
      left: auto;
    }
    .left.has-header .close {
      top: 1rem;
      right: 1.25rem;
    }
    .right .title + button.close {
      top: 1.5rem;
      left: auto;
      right: 1.875rem;
    }
    .o-bundle-builder-slide .right .title + button.close {
      left: 1.5rem;
      right: auto;
    }
    .right {
      margin-left: auto;
      transform: translateX(100%);
    }
    .medium{
      max-width:32.5rem;
    }
    .large{
      max-width: 37.5rem;
    }
    .full-screen {
      max-width: 100%;
    }
    .small{
      max-width: 32.5rem;
    }
    .title:empty {
      display: none;
    }
    .title {
      padding: 0 1.25rem;
      margin-top: 1.5rem;
      font-size: 0.75rem;
      letter-spacing: 0.135rem;
      text-transform: uppercase;
      min-height: 1.875rem;
      display: flex;
      align-items: center;
    }
    .container.is-visible { display: block; }
    .is-open .background { opacity: 1; }
    .is-open .drawer { transform: translateX(0); }

    @media only screen and (min-width: 1100px) {
      .drawer {
        border-radius: 0.875rem 0 0 0.875rem;
      }
      .left {
        border-radius: 0 0.875rem 0.875rem 0;
      }
      .close {
        left: 1.5rem;
      }
      .left .close {
        right: 1.5rem;
      }
      .right .title + button.close {
        left: auto;
        right: 1.5rem;
      }
      .title {
        padding: 0 1.75rem;
      }
    }
  </style>
  <div class="container" part="root">
    <div class="inner">
      <div class="background" part="background"></div>
      <div class="drawer" part="drawer">
        <div class="top-wrapper">
          <p class="title" data-title></p>
          <button class="close" data-close part="close">
            <svg part="close-icon" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12"><path part="icon" d="M9 3 3 9M3 3l6 6" stroke="#123247" stroke-linecap="square" stroke-linejoin="round"/></svg>
          </button>
        </div>
        <slot></slot>
      </div>
    </div>
  </div>
`;

class SlideDrawerEvent extends Event {
  public readonly slideDrawer:SlideDrawer;

  constructor(type:string, slideDrawer:SlideDrawer) {
    super(type);
    this.slideDrawer = slideDrawer;
  }
}

export class SlideDrawer extends HTMLElement {
  // Static
  public static get observedAttributes() {
    return [ 'visible', 'closable', 'direction'];
  }

  // Getters and setters
  get visible():boolean { return this.getAttribute('visible') == 'true'; }

  set visible(v:boolean) {
    console.log('SlideDrawer visible set to:', v, this);
    this.setAttribute('visible', v+'');
    this.updateVisible();
  }

  get addDrawerClass():string { return this.getAttribute('addDrawerClass') || ''; }

  set addDrawerClass(c:string) {
    this.addClass(c);
  }

  get closable() {
    return this.hasAttribute('closable') ? (
      this.getAttribute('closable') == 'true' 
    ) : true;
  }

  set closable(v:boolean) { this.setAttribute('closable', v+''); }

  get direction():SlideDrawerDirection {
    return (
      this.hasAttribute('direction') ?
      this.getAttribute('direction') as SlideDrawerDirection :
      'left'
    );
  }

  get drawerTitle():string {
    return (
      this.hasAttribute('data-title') ?
      this.getAttribute('data-title') as string : ''
    );
  }

  set direction(d:SlideDrawerDirection) {
    this.setAttribute('direction', d);
    this.updateDirection();
  }

  // slide drawer width
  get width():SlideDrawerWidth {
    return (
      this.hasAttribute('width') ?
      this.getAttribute('width') as SlideDrawerWidth :
      'large'
    );
  }

  set width(w:SlideDrawerWidth) {
    this.setAttribute('width', w);
    this.updateWidth();
  }

  // Properties
  private readonly root:HTMLDivElement|null;
  private readonly background:HTMLDivElement|null;
  private readonly drawer:HTMLDivElement|null;
  private timeout:NodeJS.Timeout|null;
  private parent:HTMLElement|null;
  private drawerTriggers:NodeListOf<HTMLButtonElement>|null = null;
  private closeBtn:HTMLButtonElement|null = null;
  private drawerTitleContainer:HTMLElement|null = null;

  // Constructor
  constructor() {
    super();

    // Init defaults
    this.timeout = null;

    // Attach shadow root
    const shadowRoot = this.attachShadow({ mode: 'open' });
    shadowRoot.appendChild(template.content.cloneNode(true));

    //@ts-ignore
    window.test = shadowRoot;

    this.classList.add(CLASS_CONTAINER);

    // Query things
    this.root = shadowRoot.querySelector('.container')!;
    this.background = shadowRoot.querySelector('.background')!;
    this.drawer = shadowRoot.querySelector('.drawer')!;
    this.parent = this.parentElement||null;
    this.drawerTriggers = document.querySelectorAll(SEL_TRIGGER);
    this.closeBtn = this.drawer.querySelector(SEL_CLOSE);
    this.drawerTitleContainer = this.drawer.querySelector(SEL_TITLE);

    // Add events
    document.addEventListener('keydown', this.onKeyPress);
    this.background?.addEventListener('click', this.onBackgroundClick);
    this.drawerTriggers?.forEach(btn => btn.addEventListener('click', this.onDocumentClick));
    this.closeBtn?.addEventListener('click', this.onCloseClick);

    // Init
    this.updateVisible();
    this.updateDirection();
    this.updateWidth();
    this.updateClass();

    if(this.getAttribute('closable') == 'false'){
      this.closeBtn?.classList.add("is-hidden")
     }

     this.addEventListener('trigger_open', (e) => {
      const { detail } = e;

      const slideOpen = detail ? 'true' : 'false';
      this.visible = slideOpen;
     })

    if (this.drawerTitle && this.drawerTitleContainer) {
      this.drawerTitleContainer.innerText = this.drawerTitle;
    }
  }

  // Private Methods
  private updateVisible() {
    if(this.timeout) clearTimeout(this.timeout);

    if(this.visible) {
      document.body.classList.add(CLASS_OPEN);
      this.root?.classList.add('is-visible');
      this.root?.classList.remove('is-closing');
      document.body.appendChild(this);
      this.timeout = setTimeout(() => this.root?.classList.add('is-open'), 16);
      this.dispatchEvent(new SlideDrawerEvent('slide-drawer-open', this));
    } else {
      // Only remove if no other drawers are open
      setTimeout(() => {
        if (!document.querySelector('slide-drawer[visible="true"]')) {
          document.body.classList.remove(CLASS_OPEN);
        }
      }, ANIMATION_SETTINGS.durationDefault);
      (this.parent||document.body).appendChild(this);

      setTimeout(() => {
        this.root?.classList.add('is-closing');
        this.root?.classList.remove('is-open');
      }, 32);
      
      this.timeout = setTimeout(() => {
        this.root?.classList.remove('is-visible');
        this.root?.classList.remove('is-closing');
      }, ANIMATION_SETTINGS.durationDefault);
      this.dispatchEvent(new SlideDrawerEvent('slide-drawer-close', this));
    }
  }

  private addClass(className) {
    if (className != '') {
      this.root?.classList.add(className);
    }
  }

  private updateDirection() {
    this.drawer?.classList.remove('left', 'right');
    this.drawer?.classList.add(this.direction == 'right' ? 'right' : 'left');
  }
  
  private updateClass() {
    if (this.classList.contains('has-header')) this.drawer?.classList.add('has-header');
  }

  //update width
  private updateWidth() {
    this.drawer?.classList.remove('large', 'small');
    this.drawer?.classList.add(this.width ? this.width : 'large');
  }

  // Public Methods

  // Events
  public onKeyPress = (e:KeyboardEvent) => {
    if(e.key == 'Escape') this.visible = false;
  }

  public onBackgroundClick = (e:MouseEvent) => {
    this.visible = false;
  }

  public onCloseClick = (e:MouseEvent) => {
    this.visible = false;
  }

  public onDocumentClick = (e:MouseEvent) => {
    const target = e.currentTarget as HTMLButtonElement;
    const attr = target.getAttribute(ATTR_TRIGGER);

    if(!attr) return;
    if(!this.matches(attr)) return;

    e.preventDefault();

    this.visible = this.closable ? !this.visible : true;
  }
}

if(!customElements.get('slide-drawer')) {
  customElements.define('slide-drawer', SlideDrawer);
}